import type React from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { ModalSelect } from '@/components/ModalSelect';
import { ScrollBlock } from '@/components/ScrollBlock';
import type { ThemeMode } from '@/helpers/enums';
import { getSelectOptionsHeight } from '@/helpers/primitives';
import { useComponentVisible } from '@/hooks/visibility';
import { useGetCommonStore } from '@/store';
import { InputStructure } from '../structures/InputStructure';
import {
  ScOptions,
  type ScOptionsProps,
  ScSelectBlock,
  ScSelectNative,
  ScSelectSearch,
} from './styled';
import type { SelectBlockProps, SelectOption } from './types';

export const SelectBlock = forwardRef(
  (
    {
      placeholder,
      options,
      selected,
      error,
      hideInputErrorColor,
      hideErrorMessage,
      hideErrorBorder,
      onSelectOption,
      disabled,
      defaultValue,
      dropdownUp,
      native,
      search,
      cols,
      openOptionsInPortal,
      mode,
      isInputAddon,
      alignBottom,
      styleScheme,
      withInsideLabel,
      testId,
      ...scProps
    }: SelectBlockProps,
    forwardedRef?: React.Ref<HTMLDivElement>
  ) => {
    const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
      useComponentVisible(false);
    const useCommonStore = useGetCommonStore();

    const isMobileDevice = useCommonStore.use.isMobileDevice();

    const handleOpen = useCallback(() => {
      if (!disabled) {
        setIsComponentVisible(!isComponentVisible);
      }
    }, [disabled, isComponentVisible, setIsComponentVisible]);

    const selectedOption = useMemo(
      () => options.find(({ value }) => value === selected),
      [selected, options]
    );

    return (
      <InputStructure
        error={error}
        hideErrorBorder={hideErrorBorder}
        hideErrorMessage={hideErrorMessage}
      >
        {native ? (
          <ScSelectNative
            error={!!error}
            hideErrorBorder={hideErrorBorder}
            mode={mode}
            styleScheme={styleScheme}
          >
            <select
              data-testid={testId}
              disabled={disabled}
              onChange={(e) => {
                onSelectOption && onSelectOption(e.target.value);
              }}
            >
              <option disabled hidden selected>
                {placeholder}
              </option>
              {options.map(({ value, name }) => (
                <option
                  key={value}
                  selected={selectedOption?.value === value}
                  value={value}
                >
                  {name}
                </option>
              ))}
            </select>
            <div className="arrow">
              <IconComponent name="whiteArrowRight" size="16px" />
            </div>
          </ScSelectNative>
        ) : (
          <ScSelectBlock
            active={isComponentVisible}
            className={isComponentVisible ? 'active' : ''}
            data-testid={testId}
            disabled={!!disabled}
            error={error != null && error !== ''}
            hideErrorBorder={hideErrorBorder}
            hideInputErrorColor={hideInputErrorColor}
            isFilled={!!selectedOption || !!defaultValue}
            isInputAddon={isInputAddon}
            mode={mode}
            ref={forwardedRef}
            styleScheme={styleScheme}
            withInsideLabel={withInsideLabel}
          >
            <div className="select-button" onClick={handleOpen} ref={openerRef}>
              <div
                className={`value ${selectedOption?.icon && !withInsideLabel ? 'with-icon' : ''}`}
              >
                {selectedOption?.icon && !withInsideLabel && (
                  <ImageComponent
                    alt="option"
                    height={24}
                    key={selectedOption.value}
                    src={selectedOption.icon}
                    width={24}
                  />
                )}
                {selectedOption
                  ? (selectedOption.nameV ?? selectedOption.name)
                  : defaultValue}
                {((!(selectedOption || defaultValue) && !!placeholder) ||
                  withInsideLabel) && (
                  <span className="placeholder">{placeholder}</span>
                )}
              </div>
              <div className="arrow">
                <IconComponent name="whiteArrowRight" size="22px" />
              </div>
            </div>
            {isComponentVisible &&
              (isMobileDevice ? (
                <ModalSelect
                  activeOption={selectedOption}
                  alignBottom={alignBottom}
                  borderRoundIconOption
                  cols={cols}
                  isPortal
                  onChange={(value) => {
                    onSelectOption && onSelectOption(value);
                    handleOpen();
                  }}
                  onClose={handleOpen}
                  openOptionsInPortal={openOptionsInPortal}
                  options={options}
                  search={search}
                  styleScheme={styleScheme}
                  testId={testId}
                  title={placeholder}
                />
              ) : (
                <OptionsBlock
                  dropdownUp={dropdownUp}
                  mode={mode}
                  onChange={(value) => {
                    onSelectOption && onSelectOption(value);
                    handleOpen();
                  }}
                  options={options}
                  ref={ref}
                  search={search}
                  selectedOption={selectedOption}
                  styleScheme={styleScheme}
                  {...scProps}
                />
              ))}
          </ScSelectBlock>
        )}
      </InputStructure>
    );
  }
);

interface OptionsBlockProps extends ScOptionsProps {
  options: SelectOption[];
  selectedOption?: SelectOption;
  onChange: (value: SelectOption['value']) => void;
  search?: boolean;
  mode?: ThemeMode;
  scrollBlockTopMargin?: number;
}

export const OptionsBlock = forwardRef<HTMLDivElement, OptionsBlockProps>(
  (
    {
      options,
      selectedOption,
      onChange,
      search,
      mode,
      scrollBlockTopMargin,
      ...props
    },
    forwardedRef
  ) => {
    const [searchValue, setSearchValue] = useState('');

    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (ref.current == null || search == null) return;

      ref.current.focus();
    }, [search]);

    const handleChangeSearchValue = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
      },
      [setSearchValue]
    );

    const filteredOptions = useMemo(() => {
      if (searchValue === '' || !search) return options;

      return options.filter(
        ({ nameS }) =>
          !nameS || nameS.toLowerCase().includes(searchValue.toLowerCase())
      );
    }, [searchValue, options, search]);

    return (
      <ScOptions {...props} ref={forwardedRef} search={search}>
        {search && (
          <ScSelectSearch styleScheme={props.styleScheme}>
            <input
              onChange={handleChangeSearchValue}
              placeholder="Search..."
              ref={ref}
              type="text"
            />
          </ScSelectSearch>
        )}
        {filteredOptions.length ? (
          <ScrollBlock
            height={getSelectOptionsHeight(filteredOptions.length, !!search)}
            mode={mode}
            scrollTopMargin={scrollBlockTopMargin}
            styleScheme={props.styleScheme}
          >
            <ul>
              {filteredOptions.map(({ icon, name, value }, index) => {
                const isActive =
                  selectedOption?.value === value ? 'active' : '';
                const isIcon = icon != null ? 'with-icon' : '';

                return (
                  <li
                    className={`${isActive} ${isIcon}`}
                    key={index}
                    onClick={() => onChange(value)}
                  >
                    <div className="content">
                      {icon && (
                        <ImageComponent
                          alt={`${name}`}
                          height={24}
                          key={value}
                          src={icon}
                          width={24}
                        />
                      )}
                      {name}
                    </div>
                    {selectedOption?.value === value && (
                      <div className="tick-circle-icon" />
                    )}
                  </li>
                );
              })}
            </ul>
          </ScrollBlock>
        ) : (
          <ul>
            <li className="not-found">Not found</li>
          </ul>
        )}
      </ScOptions>
    );
  }
);
